import { useToast } from "@crm-src/hooks/use-toast"
import { Box, Chip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { customersApi } from "@crm-src/lib/customers-api"
import type { Customer, DetailedCustomer } from "@crm-src/types/customer"

interface DeleteCustomerModalProps {
  isOpen: boolean
  onClose: () => void
  customer: Customer | DetailedCustomer | null
  onCustomerDeleted?: (deletedCustomerId: string) => void
  onNavigateToList?: () => void
}

type StatusTone = "success" | "warning" | "error" | "info" | "neutral"

const STATUS_TONE: Record<string, StatusTone> = {
  active: "success",
  at_risk: "warning",
  inactive: "neutral",
  churned: "error",
  on_hold: "info",
}

export function DeleteCustomerModal({
  isOpen,
  onClose,
  customer,
  onCustomerDeleted,
  onNavigateToList,
}: DeleteCustomerModalProps) {
  const { toast } = useToast()

  if (!customer) return null

  const handleDelete = async () => {
    try {
      await customersApi.delete(customer.id)

      toast({ title: "Success", description: "Customer deleted successfully" })
      onCustomerDeleted?.(customer.id)
      onNavigateToList?.()
    } catch (error: any) {
      console.error("Error deleting customer:", error)

      const errors: string[] | undefined = error?.response?.data?.errors
      const hasConstraint =
        errors?.some((err) => err.includes("foreign key constraint") || err.includes("still referenced"))

      if (hasConstraint) {
        toast({
          title: "Cannot Delete Customer",
          description:
            "This customer has related records (notes, activities, etc.) that prevent deletion. Please remove or transfer these records first, or contact your administrator.",
          variant: "destructive",
        })
      } else {
        toast({
          title: "Error",
          description: errors?.join(". ") || "Failed to delete customer",
          variant: "destructive",
        })
      }
      // Re-throw so ConfirmDialog keeps itself open.
      throw error
    }
  }

  const statusTone = STATUS_TONE[customer.status] ?? "neutral"

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Customer"
      // crm-api `customers#destroy` calls `Customer#soft_delete!` — the same
      // `deleted_at` stamp the prospect delete uses (both are `Customer` rows).
      // The customer leaves the lists; nothing is erased.
      description="The customer is removed from the CRM lists. The record itself is kept."
      confirmLabel="Delete Customer"
      onConfirm={handleDelete}
    >
      <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {customer.full_name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {customer.email}
            </Typography>
            {customer.company_name && (
              <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {customer.company_name}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <Chip
              label={customer.status}
              size="small"
              sx={{
                color: statusTone === "neutral" ? "text.secondary" : `${statusTone}.dark`,
                bgcolor: (t) =>
                  alpha(statusTone === "neutral" ? t.palette.text.secondary : t.palette[statusTone].main, 0.12),
              }}
            />
          </Box>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
        Are you sure you want to delete <strong>{customer.full_name}</strong>? The customer stops appearing
        in the CRM lists, search and reports.
      </Typography>
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: (t) => alpha(t.palette.warning.main, 0.3), borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: "warning.dark" }}>
          <strong>Note:</strong> Related records — notes, activities, invoices and files — are preserved,
          and the customer record is kept, so an administrator can restore it.
        </Typography>
      </Box>
    </ConfirmDialog>
  )
}
